import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { formatPrice } from 'src/helpers/price';
import AcceptedFlashProductsPeriodCell from '../../columns/AcceptedFlashProductsPeriodCell';
import FlashProductsProductInfoCell from '../../columns/FlashProductsProductInfoCell';
import IncludedFlashProductsActionCell from '../../columns/IncludedFlashProductsActionCell';
import useFlashDiscountPeriodTypes from '../../services/hooks/use-flash-discount-period-types';
import { FlashProductTransition } from '../../services/models/flash-discount';
import type { IAcceptedFlashProductListItem } from '../../services/models/flash-discount';
import type { IDataTableRef } from 'src/components/DataTable/shared/types';

export interface IUseAcceptedFlashProductsColumnsOptions {
  /** Başlayacak / Devam Eden için İşlemler kolonu gösterilir. */
  transition?: FlashProductTransition | null;
  /** İşlemler hücresinde kullanılır (invalidate, clearSelection). */
  dataTableRef?: React.RefObject<IDataTableRef | null>;
  /** Satır "İptal Et" tıklanınca onay modalı açılsın diye çağrılır (id ile). */
  onRequestRowCancel?: (id: number) => void;
}

export default function useAcceptedFlashProductsColumns(options: IUseAcceptedFlashProductsColumnsOptions = {}) {
  const { transition, dataTableRef, onRequestRowCancel } = options;
  const flashDiscountPeriodTypes = useFlashDiscountPeriodTypes();

  const showActionsColumn =
    !!transition &&
    [FlashProductTransition.waiting, FlashProductTransition.active].includes(transition);

  const columns = useMemo(() => {
    if (!flashDiscountPeriodTypes.isSuccess) return [];

    const ColumnHelper = createColumnHelper<IAcceptedFlashProductListItem>();

    const periodColumns = flashDiscountPeriodTypes.data?.map((period) =>
      ColumnHelper.display({
        id: `period-${period.id}`,
        header: period.title,
        cell: (info) => {
          return <AcceptedFlashProductsPeriodCell cellContext={info as any} period={period as any} />;
        },
      }),
    );

    const actionsColumn = showActionsColumn &&
      dataTableRef &&
      ColumnHelper.display({
        id: 'actions',
        header: 'İşlemler',
        cell: (info) => (
          <IncludedFlashProductsActionCell
            cellContext={info}
            dataTableRef={dataTableRef}
            onRequestRowCancel={onRequestRowCancel}
          />
        ),
        meta: {
          columnClassName: 'min-w-200px',
          cellClassName: '',
        },
      });

    return [
      /** Column => Flaş Ürün Bilgileri */
      ColumnHelper.display({
        id: 'info',
        header: 'Flaş Ürün Bilgileri',
        cell: (info) => {
          return <FlashProductsProductInfoCell cellContext={info as any} />;
        },
        meta: {
          columnClassName: 'min-w-350px product-name bg-light border-end ps-4',
          cellClassName: 'ps-4 product-name border-end',
        },
      }),

      /** Column => Güncel Satış Fiyatı */
      ColumnHelper.display({
        id: 'price',
        header: 'Güncel Satış Fiyatı / Komisyon',
        cell: (info) => {
          const item = info?.row?.original;
          const price = item?.price;
          const commission = item?.commission;

          return (
            <div className="d-flex flex-column gap-4">
              <div className="d-flex gap-2 align-items-center">
                <b>Güncel Satış Fiyatı:</b>
                <span>{formatPrice(price, { suffix: ' TL' })}</span>
              </div>

              <div className="d-flex gap-2 align-items-center">
                <b>Komisyon:</b>
                <span>{commission?.percentage ? `%${commission?.percentage}` : '-'}</span>
              </div>
            </div>
          );
        },
      }),

      /** Column => Flaş Ürün Periyotları */
      ...(periodColumns ?? []),

      /** Column => İşlemler (Başlayacak / Devam Eden) */
      ...(actionsColumn ? [actionsColumn] : []),
    ];
  }, [flashDiscountPeriodTypes, showActionsColumn, dataTableRef, onRequestRowCancel]);

  return columns;
}
